import { carrierKindTag, contactTypeCategory, loaTag, ownerTag, supplyVesselTypeTag, targetSpeciesCategory, vesselKindCategory, vesselTypeCategory } from '#details/constants';
import { Contact, DetailsPatch } from '#details/model';
import { maxLoa, minLoa } from '#details/validator';
import { photographsTypeCategory, photographType } from '#photographs/constants';
import { usePhotoQualityHelper } from '#photographs/model';
import { isRavRecord, isDelisted, RecordIdentifier } from '#record/model';
import { RecordValidationData, RecordValidator, useGenericValidator } from '#record/validator';
import { Submission } from '#submission/model';
import { RecordIssue } from '#submission/validator';
import { imoscheme, regnoscheme } from '#vid/constants';
import { useVID } from '#vid/model';
import { Tag } from 'apprise-core/tag';
import { utils } from 'apprise-core/utils';
import { useT } from 'apprise-frontend-core/intl/language';
import { useOracle } from 'apprise-frontend-iam/authz/oracle';
import { isBytestream } from 'apprise-frontend-streams/model';
import { useTagCache } from 'apprise-frontend-tags/cache';
import { useTagStore } from 'apprise-frontend-tags/tag/store';
import partition from 'lodash/partition';
import { avlType } from './constants';
import { AvlPatch } from './model';
import { delistingReasonScrapped, delistingType } from '#delisting/constants';


export const useAvlValidation = (): RecordValidator<AvlPatch> => {

    const t = useT()

    const detailValidator = useAvlDetailValidator()
    const avlDecalarationValidator = useAvlDeclarationValidator()


    const combinedValidator = (data: RecordValidationData<AvlPatch>[], submission: Submission<AvlPatch>) => {

        // isolate unmatched rav records so we can report a single errors for all the missing data.   
        const [ravunmatched, others] = partition(data, ({ patch, current }) => isRavRecord(patch) && (!patch.uvi || !current))

        const detailIssues = detailValidator(others, submission)
        // const photoIssues = photoValidator(others, submission)

        // but validate declarations in the patch on all records. 
        const avlIssues = avlDecalarationValidator(data, submission)

        //  merge unmatched error with avl issues.
        const unmatchedIssues = ravunmatched.reduce((acc, { patch }) => {

            // if patch has a uvi, error messages are already produced in generic validator, for missing UVI we must add explicitly
            acc[patch.id] = patch.uvi ? avlIssues[patch.id] : [{ message: t('valid.missing_uvi'), type: 'error', location: 'uvi' }, ...avlIssues[patch.id]]

            return acc
        }, {})

        // Merging the keys to ensure that all the collected issues are taken into account!
        const allOtherIssuesCombined = utils().dedup([...Object.keys(detailIssues), ...Object.keys(avlIssues)]).reduce((acc, key) => ({

            ...acc, [key]: [...detailIssues[key] ?? [], ...avlIssues[key] ?? []]

        }), {} as Record<RecordIdentifier, RecordIssue[]>)


        return { ...unmatchedIssues, ...allOtherIssuesCombined }

    }


    return useGenericValidator(avlType, combinedValidator)


}

export const useAvlDeclarationValidator = (): RecordValidator<AvlPatch> => {

    const t = useT()

    const tags = useTagStore().allTagsOfCategory(targetSpeciesCategory)

    const isValidSpecies = (id: string) => tags.findIndex(sp => sp.id === id) > -1

    const currentYear = new Date().getFullYear()

    return (data, _) => {

        return data.reduce((acc, { patch, current }) => {

            const { avl } = patch

            const issues: RecordIssue[] = []

            if (current?.details.vesselKind === carrierKindTag) {
                issues.push({ message: t('valid.avl_not_allowed_for_carrier'), type: 'warning', location: vesselKindCategory })
            }

            avl.declarations.forEach((decl, idx) => {

                if (!decl.tenant)
                    issues.push({ message: t('valid.avl_missing_tenant'), type: 'error', location: `${idx}_avl_tenant` })

                if (!decl.species)
                    issues.push({ message: t('valid.avl_missing_species'), type: 'error', location: `${idx}_avl_species` })
                else
                    if (!isValidSpecies(decl.species))
                        issues.push({ message: t('valid.avl_wrong_species'), type: 'error', location: `${idx}_avl_species` })



                if (!decl.charterer) {
                    issues.push({ message: t('valid.avl_missing_charterer_address'), type: 'error', location: `${idx}_avl_charterer_address` })
                    issues.push({ message: t('valid.avl_missing_charterer_name'), type: 'error', location: `${idx}_avl_charterer_name` })
                }
                else {

                    if (!decl.charterer.address)
                        issues.push({ message: t('valid.avl_missing_charterer_address'), type: 'error', location: `${idx}_avl_charterer_address` })


                    if (!decl.charterer.name)
                        issues.push({ message: t('valid.avl_missing_charterer_name'), type: 'error', location: `${idx}_avl_charterer_name` })

                }


                if (!decl.year)
                    issues.push({ message: t('valid.avl_missing_year'), type: 'error', location: `${idx}_avl_year` })

                else {

                    const year = Number(decl.year)

                    if (!Number.isInteger(year) || year < 1000 || year > 9999)
                        issues.push({ message: t('valid.avl_invalid_year'), type: 'error', location: `${idx}_avl_year` })

                    else {

                        const currentlyDelisted = isDelisted(current)

                        if (currentYear <= year) {
                            
                            issues.push({ message: t('valid.avl_current_year', { year: currentYear - 1 }), type: 'error', location: `${idx}_avl_year` })

                        // for updates made 

                        } else if (patch.uvi) {

                            const latest = current?.avl?.declarations[0].year

                            if (latest && decl.year < latest)
                                issues.push({ message: t('valid.avl_past_year', { latest, year, }), type: 'error', location: `${idx}_avl_year` })

                        }

                        if (currentlyDelisted && current?.[delistingType]?.reason === delistingReasonScrapped) {

                            const delistingDate = current?.[delistingType]?.date
                            const delistingYear = delistingDate ? new Date(delistingDate).getFullYear() : 0

                            if (year > delistingYear) {
                                issues.push({ message: t('valid.avl_scrapped_vessel'), type: 'error', location: `${idx}_avl_year` })
                            }

                        }

                    }

                }

            })


            return { ...acc, [patch.id]: issues }

        }, {})


    }

}


export const useAvlDetailValidator = (): RecordValidator<DetailsPatch> => {

    const t = useT()

    const vids = useVID()
    const logged = useOracle()

    const tagmap = useTagCache()

    const lowQualityMessage = usePhotoQualityHelper()

    const tags = useTagStore()

    return (data, _) => {

        const imomap = utils().index(data.map(d => d.patch)).byGroup(t => vids.find(imoscheme, t.details?.identifiers.filter(vid => !vids.isSpecialValue(vid))))
        const regnomap = utils().index(data.map(d => d.patch)).byGroup(t => vids.find(regnoscheme, t.details?.identifiers.filter(vid => !vids.isSpecialValue(vid))))
        //const ircsmap = utils().index(data.map(d => d.patch)).byGroup(t => vids.find(ircsscheme, t.details?.identifiers.filter(vid => !vids.isSpecialValue(vid))))
        const namemap = utils().index(data.map(d => d.patch)).byGroup(t => t.details?.name?.toLowerCase().trim())

        return data.reduce((acc, { patch, current }) => {

            const { details, scope } = patch

            // we don't force "fix me first" policy on RAV vessels for AVL subs.
            if (scope === 'rav')
                return {}

            const issues: RecordIssue[] = []

            const imo = vids.find(imoscheme, details.identifiers)
            const currentimo = current ? vids.find(imoscheme, current?.details.identifiers) : undefined

            const regno = vids.find(regnoscheme, details.identifiers)

            if (!details.name)
                issues.push({ message: t('valid.missing_name'), type: 'error', location: "name" })

            if (details.name && namemap[details.name.toLowerCase().trim()].length > 1)
                issues.push({ message: t('valid.duplicate_name', { name: details.name, length: namemap[details.name.toLowerCase().trim()].length }), type: 'warning', location: "name" })

            // if (!imo)
            //     issues.push({ message: t('valid.missing_imo'), type: 'warning', location: imoscheme })

            if (imo && !vids.isSpecialValue(imo)) {

                if (!vids.isValidIMO(imo))
                    issues.push({ message: t('valid.invalid_imo'), type: 'error', location: imoscheme })


                if (currentimo && !vids.isSpecialValue(currentimo) && currentimo !== imo)
                    issues.push({ message: t('valid.changed_imo', { imo: currentimo }), type: logged.hasNoTenant() ? 'warning' : 'error', location: imoscheme })

                if (imomap[imo].length > 1)
                    issues.push({ message: t('valid.duplicate_imo', { imo, length: imomap[imo].length }), type: 'error', location: imoscheme })

            }


            if (!regno)
                issues.push({ message: t('valid.missing_regno'), type: 'error', location: regnoscheme })

            if (regno && !vids.isSpecialValue(regno) && regnomap[regno].length > 1)
                issues.push({ message: t('valid.duplicate_regno', { regno, length: regnomap[regno].length }), type: 'error', location: regnoscheme })


            if (!details.vesselType)
                issues.push({ message: t('valid.missing_type'), type: 'error', location: vesselTypeCategory })

            if (details.vesselType && !tagmap.id2tag(details.vesselType).of(vesselTypeCategory))
                issues.push({ message: t('valid.invalid_type', { type: details.vesselType }), type: 'error', location: vesselTypeCategory })

            if (details.vesselType === supplyVesselTypeTag && details.vesselType !== current?.details.vesselType)
                issues.push({ message: t('valid.invalid_supply_type'), type: 'warning', location: vesselTypeCategory })

            if (!details.specifications[loaTag])
                issues.push({ message: t('valid.missing_loa'), type: 'error', location: loaTag })

            if (details.specifications[loaTag]) {

                if (parseFloat(details.specifications[loaTag]) <= minLoa)
                    issues.push({ message: t('valid.invalid_loa'), type: 'error', location: loaTag })
                else

                    if (parseFloat(details.specifications[loaTag]) > maxLoa)
                        issues.push({ message: t('valid.invalid_loa_range', { max: maxLoa }), type: 'warning', location: loaTag })


            }


            // if (!details.specifications[tonnageTag] && (!current || !current.details.specifications[grtTag]))
            //     issues.push({ message: t('valid.missing_tonnage'), type: 'warning', location: tonnageTag })

            // if (details.specifications[tonnageTag]) {

            //     if (parseFloat(details.specifications[tonnageTag]) <= minTonnage)
            //         issues.push({ message: t('valid.invalid_tonnage'), type: 'error', location: tonnageTag })

            //     else if (parseFloat(details.specifications[tonnageTag]) > maxTonnage)
            //         issues.push({ message: t('valid.invalid_tonnage_range', { max: maxTonnage }), type: 'warning', location: tonnageTag })

            // }

            // if (details.specifications[totalVolumeTag]) {

            //     if (parseFloat(details.specifications[totalVolumeTag]) <= minVolume)
            //         issues.push({ message: t('valid.invalid_totvol'), type: 'error', location: totalVolumeTag })

            //     else

            //         if (parseFloat(details.specifications[totalVolumeTag]) > maxVolumeFV)
            //             issues.push({ message: t('valid.invalid_totvol_range', { max: maxVolumeFV }), type: 'warning', location: totalVolumeTag })

            // }

            // if (details.range && !tagmap.id2tag(details.range).of(vesselRangeCategory))
            //     issues.push({ message: t('valid.invalid_range', { range: details.range }), type: 'error', location: vesselRangeCategory });


            // (details.gears ?? []).filter(g => !tagmap.id2tag(g).of(vesselGearCategory)).forEach(gear => issues.push({ message: t('valid.invalid_gear', { gear }), type: 'error', location: gear }))





            //----- contacts ------------------------------------------------------------------------------------------------------------------------------------

            // carriers require only owner and operator types, other vessels require all contact types.
            const mandatoryContact = (contact: Contact) => [ownerTag].includes(contact.type)

            details.contacts.filter(mandatoryContact).forEach(contact => {

                // Just owner name is mandatory for AVL/Ext declaration
                if (!contact.name)
                    issues.push({ message: t('valid.missing_contact_name', { name: tagmap.id2name(contact.type).of(contactTypeCategory) }), type: 'error', location: `${contact.type}-name` })

                // if (!contact.address)
                //     issues.push({ message: t('valid.missing_contact_address', { name: tagmap.id2name(contact.type).of(contactTypeCategory) }), type: 'warning', location: `${contact.type}-address` })

                // if (isOperatingCompany(contact) && !contact.regno)
                //     issues.push({ message: t('valid.missing_company_regno', { name: tagmap.id2name(contact.type).of(contactTypeCategory) }), type: 'warning', location: `${contact.type}-regno` })

            })

            const { photos = [] } = patch[photographType]

            const phototags = (tags.allTagsOfCategory(photographsTypeCategory, true) ?? []).sort((pt1, _) => isBytestream(photos.find(p => p.type === pt1.id)?.ref) ? -1 : 1)

            const validatePhoto = (tag: Tag) => {

                const photoref = photos.find(p => p.type === tag.id)?.ref

                // we have at least a reference.
                if (photoref) {

                    // resolved image, check quality.
                    if (isBytestream(photoref)) {

                        const message = lowQualityMessage(photoref, tag.id)

                        if (message)
                            issues.push({ message, type: 'warning', location: `${tag.id}-quality` })

                    }
                }

            }
            phototags.forEach(validatePhoto)


            return { ...acc, [patch.id]: issues }

        }, {})


    }
}
